import { Link, useParams } from "react-router-dom";
import { Calendar, User, ArrowLeft, Facebook, Linkedin, Twitter } from "lucide-react";
import { Button } from "../components/ui/button";
import { Separator } from "../components/ui/separator";
import { blogPostsData, referenceImages, referenceFilterLabels } from "../data/content";

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = blogPostsData[slug];

  if (!post) {
    return (
      <div className="py-32 text-center">
        <h1 className="text-2xl text-slate-900">Bejegyzés nem található</h1>
        <Link to="/blog" className="text-amber-600 mt-4 inline-block">Vissza a bloghoz</Link>
      </div>
    );
  }

  // Get related reference images
  const relatedRefs = post.refKey ? (referenceImages[post.refKey] || []).slice(0, 4) : [];
  const refLabel = post.refKey ? (referenceFilterLabels[post.refKey] || "") : "";

  return (
    <div data-testid="blog-post-page">
      {/* Hero */}
      <section className="relative py-8 sm:py-10 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-12">
          <Link to="/blog" className="inline-flex items-center text-stone-400 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Vissza a bloghoz
          </Link>
          <div className="flex items-center gap-4 text-sm text-stone-400 mb-4">
            <span className="bg-amber-600 text-white px-3 py-1">
              {post.category}
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-white leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {post.title}
          </h1>
          <div className="flex items-center gap-6 mt-6 text-stone-400 text-sm">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {post.author}
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {post.date}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-12 -mt-8 relative z-10">
          <div className="aspect-[16/9] overflow-hidden shadow-xl">
            <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-12">
          <article 
            className="prose prose-lg prose-slate max-w-none
              prose-headings:font-semibold prose-headings:text-slate-900
              prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-4
              prose-p:text-slate-600 prose-p:leading-relaxed
              prose-ul:text-slate-600
              prose-li:marker:text-amber-500
              prose-a:text-amber-600 prose-a:no-underline hover:prose-a:underline
              prose-strong:text-slate-900"
            dangerouslySetInnerHTML={{ __html: post.content }}
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
          />

          <Separator className="my-12" />

          {/* Share */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <span className="text-slate-500 text-sm">Megosztás:</span>
            <div className="flex items-center gap-2">
              <a 
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-slate-100 flex items-center justify-center hover:bg-blue-600 hover:text-white transition-colors"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a 
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-slate-100 flex items-center justify-center hover:bg-sky-500 hover:text-white transition-colors"
              >
                <Twitter className="w-4 h-4" />
              </a>
              <a 
                href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(post.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-slate-100 flex items-center justify-center hover:bg-blue-700 hover:text-white transition-colors"
              >
                <Linkedin className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Related References */}
      {relatedRefs.length > 0 && (
        <section className="py-12 sm:py-16 bg-stone-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-12">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl sm:text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Kapcsolódó referenciák
              </h2>
              <Link 
                to={`/referenciak?filter=${post.refKey}`}
                className="text-amber-600 text-sm font-medium hover:text-amber-700"
              >
                Összes megtekintése
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {relatedRefs.map((img) => (
                <Link 
                  key={img.id}
                  to={`/referenciak?filter=${post.refKey}`}
                  className="aspect-square overflow-hidden group"
                >
                  <img 
                    src={img.src} 
                    alt={img.alt} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                  />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Kérdése van?
          </h2>
          <p className="text-slate-600 mb-8">
            Vegye fel velünk a kapcsolatot és segítünk megtalálni az Önnek legmegfelelőbb megoldást!
          </p>
          <Button 
            asChild
            className="rounded-none px-10 py-6 bg-amber-600 text-white hover:bg-amber-700 font-medium text-sm tracking-wider uppercase"
          >
            <Link to="/kapcsolat">Kapcsolatfelvétel</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default BlogPostPage;
